// src/components/hooks/useUploadEmpresaLogo.ts

import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { EmpresaDetalle } from '@/components/hooks/useEmpresa';

interface UseUploadEmpresaLogoProps {
  empresa: EmpresaDetalle;
  updateEmpresaLocal: (patch: Partial<EmpresaDetalle>) => void;
}

export const useUploadEmpresaLogo = ({
  empresa,
  updateEmpresaLocal,
}: UseUploadEmpresaLogoProps) => {
  const [loadingLogo, setLoadingLogo] = useState(false);

  // ============================================================
  // SUBIR LOGO
  // ============================================================
  const uploadLogo = useCallback(
    async (file: File | null) => {
      if (!file || !empresa) return;

      setLoadingLogo(true);

      try {
        const id_empresa = empresa.id_empresa;
        const extension = file.name.split('.').pop();
        const path = `logos/${id_empresa}.${extension}`;

        // 1) eliminar logo previo (si existe)
        await supabase.storage.from('bookoll').remove([path]);

        // 2) subir el archivo
        const { error: uploadError } = await supabase.storage
          .from('bookoll')
          .upload(path, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type,
          });

        if (uploadError) {
          Sentry.captureException(uploadError, { extra: { id_empresa, path } });
          toast.error('Error al subir el logo.');
          return;
        }

        // 3) obtener URL pública, versionada: la ruta es siempre la misma y
        //    sin versión el navegador/CDN seguirían sirviendo el logo anterior.
        const publicUrl = `${
          supabase.storage.from('bookoll').getPublicUrl(path).data.publicUrl
        }?v=${Date.now()}`;

        // 4) actualizar en BD
        const { error } = await supabase
          .from('empresas')
          .update({
            logo: publicUrl,
            usuario_edicion: localStorage.getItem('id_usuario'),
          })
          .eq('id_empresa', id_empresa);

        if (error) {
          Sentry.captureException(error, { extra: { id_empresa } });
          toast.error('Error al guardar el logo en la empresa.');
          return;
        }

        // 5) actualizar estado local
        updateEmpresaLocal({ logo: publicUrl });
        localStorage.setItem('logo_empresa', publicUrl);
        // El menú lateral tiene su propia copia de la empresa: se le avisa.
        window.dispatchEvent(
          new CustomEvent<string>('empresa:logo', { detail: publicUrl })
        );

        toast.success('Logo actualizado correctamente.');
      } catch (err) {
        Sentry.captureException(err, {
          extra: { id_empresa: empresa.id_empresa },
        });
        toast.error('Error inesperado al subir logo.');
      } finally {
        setLoadingLogo(false);
      }
    },
    [empresa, updateEmpresaLocal]
  );

  return {
    loadingLogo,
    uploadLogo,
  };
};
